// shared/api/PixieWorldApiService.ts
import axios, { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios'
import axiosRetry from 'axios-retry'
import { setToast } from 'entities/toast'

import { store } from '@/shared/store'

import { PIXIEWORLD_API_URI } from '../consts'

const createPixieWorldClient = (token?: string) => {
  const client = axios.create({
    baseURL: PIXIEWORLD_API_URI,
    headers: {
      'Accept': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    },
  })

  // Only add retries and error handling on client-side
  if (typeof window !== 'undefined') {
    axiosRetry(client, {
      retries: 1,
      retryDelay: (retryCount: number) => retryCount * 1000,
      retryCondition: (error) => error.response === undefined,
    })
  }

  return client
}

export type CustomPixieWorldApiServiceOptions = {
  suppressToast?: boolean,
  customErrorHandler?: (error: unknown) => void
}

export class PixieWorldApiService {
  private instance: AxiosInstance

  private isRefreshing = false

  private refreshSubscribers: ((token: string) => void)[] = []

  private accessToken: string | null = null

  constructor(token?: string) {
    this.instance = createPixieWorldClient(token)
    this.accessToken = token || null
  }

  private updateAuthorizationHeader(useAuth = true) {
    if (useAuth && this.accessToken) {
      this.instance.defaults.headers.common.Authorization = `Bearer ${this.accessToken}`
    } else {
      delete this.instance.defaults.headers.common.Authorization
    }
  }

  setAccessToken(accessToken?: string | null) {
    this.accessToken = accessToken || null
    this.updateAuthorizationHeader(!!accessToken)
  }


  setupTokenRefreshInterceptor(
    getToken: () => string | null,
    refreshToken: () => Promise<void>,
  ) {
    // (Client-side only) Request interceptor to add token
    const requestInterceptor = this.instance.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        if (typeof window !== 'undefined' && !config.headers.Authorization) {
          const token = getToken()

          if (token) {
            config.headers.Authorization = `Bearer ${token}`
          }
        }

        return config
      },
    )

    // Response interceptor to handle token refresh
    const responseInterceptor = this.instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config

        // If error is 401 and we haven't tried refreshing yet
        if (typeof window !== 'undefined' && error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            // If already refreshing, wait for new token
            try {
              const token = await new Promise<string>((resolve) => {
                this.refreshSubscribers.push((tok: string) => {
                  resolve(tok)
                })
              })

              originalRequest.headers.Authorization = `Bearer ${token}`

              return await this.instance(originalRequest)
            } catch (err) {
              return Promise.reject(err)
            }
          }

          // Start refresh process
          originalRequest._retry = true
          this.isRefreshing = true

          try {
            await refreshToken()

            const token = getToken()

            if (!token) throw new Error('No token after refresh')

            // Notify subscribers and reset
            this.refreshSubscribers.forEach((callback) => callback(token))
            this.refreshSubscribers = []

            // Retry original request
            originalRequest.headers.Authorization = `Bearer ${token}`

            return await this.instance(originalRequest)
          } catch (err) {
            return await Promise.reject(err)
          } finally {
            this.isRefreshing = false
          }
        }

        return Promise.reject(error)
      },
    )

    return { requestInterceptor, responseInterceptor }
  }

  removeTokenRefreshInterceptor(interceptor: {
    requestInterceptor: number
    responseInterceptor: number
  }) {
    this.instance.interceptors.request.eject(interceptor.requestInterceptor)
    this.instance.interceptors.response.eject(interceptor.responseInterceptor)
  }

  async request<T>(
    config: AxiosRequestConfig,
    options?: CustomPixieWorldApiServiceOptions,
  ): Promise<T> {
    try {
      const response = await this.instance.request<T>(config)

      return response.data
    } catch (error) {
      if (options?.customErrorHandler) {
        options.customErrorHandler(error)
      }
      else if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        return Promise.reject(error)
      }

      // Re-throw the error to allow the calling code to handle it
      throw error
    }
  }

  async get<T>(url: string, params?: object): Promise<T> {
    const response = await this.request<T>({
      url,
      method: 'GET',
      params,
    })

    return response
  }

  async post<T>(url: string, data?: object): Promise<T> {
    const response = await this.request<T>({
      url,
      method: 'POST',
      data,
    })

    return response
  }

  /*
    Uploads a file to the server using a PUT request with a FormData object.
  */
  async upload<TResult>(endpoint: string, formData: FormData, onProgress?: (progress: number) => void, useAuth?: boolean): Promise<TResult> {
    this.updateAuthorizationHeader(useAuth)

    try {
      const response = await this.instance.put(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1))

            onProgress(percentCompleted)
          }
        },
      })

      if (!response.data) {
        throw new Error('Upload response contained no data')
      }

      return response.data
    } catch (error) {
      // Only show toasts on client-side
      if (typeof window !== 'undefined') {
        if (axios.isAxiosError(error)) {
          const errorMessage = error.response?.data?.message
            ? `<strong>Message:</strong> ${error.response?.data?.message}`
            : `An error occurred during file upload: ${error.message}.`

          store.dispatch(setToast({
            open: true,
            severity: 'error',
            title: `Error ${error.code}`,
            content: `${errorMessage}<br />
                <strong>Method:</strong> POST<br />
                <strong>Endpoint:</strong> ${endpoint}
              `,
          }))
        } else {
          store.dispatch(setToast({
            open: true,
            severity: 'error',
            title: 'Error',
            content: `An error occurred during file upload.<br />
              <strong>Method:</strong> POST <br />
              <strong>Endpoint:</strong> ${endpoint}`,
          }))
        }
      }

      throw error
    }
  }
}

// Singleton instance for client-side use
export const pixieWorldApiClient = new PixieWorldApiService()

// Helper for server-side use
export const createPixieWorldApiClient = (token?: string) => new PixieWorldApiService(token)